"use client";

import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import type { ProducerOption } from "@/components/ProducerCombobox";

export type { ProducerOption };

type Props = {
  producers: ProducerOption[];
  selectedId: string;
  onSelect: (producerId: string | null) => void;
  disabled?: boolean;
};

export function ProducerFilterCombobox({
  producers,
  selectedId,
  onSelect,
  disabled,
}: Props) {
  const [open, setOpen] = React.useState(false);

  const selected = producers.find((p) => String(p.id) === selectedId);
  const label = selected?.name?.trim() || "All producers";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="h-9 w-[280px] max-w-full justify-between font-normal"
        >
          <span className="truncate">{label}</span>
          <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search producer…" className="h-9" />
          <CommandList>
            <CommandEmpty>No producer found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__all__"
                keywords={["all", "producers", "any", "clear"]}
                onSelect={() => {
                  onSelect(null);
                  setOpen(false);
                }}
              >
                All producers
                <Check
                  className={cn(
                    "ml-auto size-4",
                    !selectedId ? "opacity-100" : "opacity-0",
                  )}
                />
              </CommandItem>
              {producers.map((producer) => {
                const idStr = String(producer.id);
                const name = producer.name?.trim() || `Producer #${producer.id}`;
                return (
                  <CommandItem
                    key={producer.id}
                    value={name}
                    keywords={[idStr, String(producer.id)]}
                    onSelect={() => {
                      onSelect(idStr);
                      setOpen(false);
                    }}
                  >
                    {name}
                    <Check
                      className={cn(
                        "ml-auto size-4",
                        selectedId === idStr ? "opacity-100" : "opacity-0",
                      )}
                    />
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
